import { memo, useCallback, useMemo } from 'react';
import { Button } from '@coreai/fluentui-react';
import { ArrowLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import type { Connector } from '@/services/operationsService';
import type { ConnectorManifest } from '@/types/connections';
import type { OperationDefinition } from '@/types/operations';
import {
  connectorToManifest,
  discoveryOperationToDefinition,
} from '@/services/operationsService';
import {
  isManagedApiTier,
  type ConnectorSearchGroup,
} from '@/services/searchOperationsService';
import { AzureConnectorSection } from './AzureConnectorSection';
import { ConnectorSection } from './ConnectorSection';
import type { useOperationPickerPanelStyles } from './OperationPickerPanel.styles';

/** Inline preview matches per connector before suggesting drill-in. */
const MAX_MATCHES_PER_CONNECTOR_PREVIEW = 5;

type PanelStyles = ReturnType<typeof useOperationPickerPanelStyles>;

type SearchConnectorGroupCardProps = {
  group: ConnectorSearchGroup;
  /** Current search term, threaded down so matches highlight inline. */
  searchQuery: string;
  onOperationSelect: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  /**
   * Receives this card's own `group` so the parent can supply one stable
   * callback to every card — keeps `React.memo` effective when sibling
   * cards re-render.
   */
  onViewAll: (group: ConnectorSearchGroup) => void;
  styles: PanelStyles;
};

/**
 * One connector's matched operations rendered as a `ConnectorSection` so
 * the search view visually matches the browse view (sticky header, brand
 * accent rail, no per-row icons). Followed by a subtle "View all
 * operations from {connector}" link that drills into the full view.
 *
 * Memoized so toggling drill-in (which re-renders the parent) doesn't
 * re-render every card in the search results list.
 */
export const SearchConnectorGroupCard = memo(({
  group,
  searchQuery,
  onOperationSelect,
  onViewAll,
  styles,
}: SearchConnectorGroupCardProps) => {
  const previewMatches = group.matches.slice(0, MAX_MATCHES_PER_CONNECTOR_PREVIEW);
  // Read the group's pre-computed tier — re-deriving from `connectorId`
  // would fail on synthetic `displayName:` keys for malformed ids.
  const isBuiltIn = !isManagedApiTier(group.bestConnectorTier);

  const operations = useMemo(
    () => previewMatches.map((m) => discoveryOperationToDefinition(m.operation)),
    [previewMatches],
  );

  const handleViewAll = useCallback(() => onViewAll(group), [onViewAll, group]);

  const matchCountLabel = group.totalMatches === 1 ? '1 match' : `${group.totalMatches} matches`;

  return (
    <section aria-label={`${group.connectorDisplayName} (${matchCountLabel})`}>
      <ConnectorSection
        connectorName={group.connectorDisplayName}
        operations={operations}
        onOperationSelect={onOperationSelect}
        defaultExpanded
        isBuiltIn={isBuiltIn}
        searchQuery={searchQuery}
        noMargin
      />
      <button
        type="button"
        className={styles.searchViewAllButton}
        onClick={handleViewAll}
        aria-label={`View all operations from ${group.connectorDisplayName}`}
      >
        <span>View all operations from {group.connectorDisplayName}</span>
        <ChevronRightRegular />
      </button>
    </section>
  );
});

SearchConnectorGroupCard.displayName = 'SearchConnectorGroupCard';

type SearchConnectorDetailProps = {
  group: ConnectorSearchGroup;
  /** Hydrated Azure connector from the legacy groups hook, if loaded. */
  hydratedAzureConnector?: Connector;
  /** Built-in connector operations from the legacy groups hook, if available. */
  builtInOperations?: OperationDefinition[];
  actionType?: 'triggers' | 'actions';
  onOperationSelect: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  onBack: () => void;
  styles: PanelStyles;
};

/**
 * Drill-in view for a single connector from search results. Azure managed
 * APIs lazy-fetch via `AzureConnectorSection`; built-ins use the cached
 * grouped data when available, falling back to the matched operations so
 * drill-in is never empty even if a lazy hook hasn't loaded yet.
 */
export const SearchConnectorDetail = ({
  group,
  hydratedAzureConnector,
  builtInOperations,
  actionType,
  onOperationSelect,
  onBack,
  styles,
}: SearchConnectorDetailProps) => {
  const isAzure = isManagedApiTier(group.bestConnectorTier);
  const firstApi = group.matches[0]?.operation.properties?.api;

  // Synthesize a manifest from the search group when the Azure groups hook
  // hasn't loaded this connector yet — drill-in must always render.
  //
  // Note: `azureConnectorsById` in the picker keys connectors by their full
  // ARM id, but `ConnectorSearchGroup.connectorId` is normalized to a short
  // lowercased form, so `hydratedAzureConnector` is effectively never passed
  // for search drill-in today and this branch is the common path. Using `||`
  // rather than `??` makes the brand-color fallback also defend against
  // empty strings, and `#0078D4` matches `hexToColor`'s fallback so the
  // accent rail is visible on the dark canvas.
  const manifest: ConnectorManifest = useMemo(() => {
    if (hydratedAzureConnector) return connectorToManifest(hydratedAzureConnector);
    return {
      id: group.connectorId,
      name: firstApi?.name ?? group.connectorId,
      displayName: group.connectorDisplayName,
      iconUri: firstApi?.iconUri ?? '',
      brandColor: firstApi?.brandColor || '#0078D4',
      ...(firstApi?.description && { description: firstApi.description }),
    };
  }, [hydratedAzureConnector, firstApi, group]);

  return (
    <div className={styles.searchDetailContainer}>
      <Button
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        onClick={onBack}
        className={styles.searchDetailBack}
        aria-label="Back to search results"
      >
        Back to results
      </Button>
      {isAzure ? (
        <AzureConnectorSection
          connector={manifest}
          {...(actionType && { actionType })}
          onOperationSelect={onOperationSelect}
          isExpanded
          onExpandedChange={() => {}}
          isCollapsible={false}
        />
      ) : (
        <ConnectorSection
          connectorName={group.connectorDisplayName}
          operations={
            builtInOperations ??
            group.matches.map((m) => discoveryOperationToDefinition(m.operation))
          }
          onOperationSelect={onOperationSelect}
          isExpanded
          onExpandedChange={() => {}}
          isCollapsible={false}
        />
      )}
    </div>
  );
};
